import { Box, Progress, Table, Text } from "@mantine/core"
import { EyeIcon, HandshakeIcon, PencilSimpleIcon, UserPlusIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { IDeliveryRoute } from "../../apis/delivery-route"
import { renderFullName } from "../../utils/render-name"
import { DeliveryRowAction } from "../delivery/action-controls"
import { DotPill, TableFrame, type Tone } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"
import RenderText from "../ui/render-text"
import classes from "./RouteTable.module.css"
import { RouteProgress, routeLoadedPct, routeProgress } from "./route-progress"
import { useVehicleClassLabel } from "./vehicle-class"

const DASH = "-"

type RouteStateKind =
  | "WAITING_DRIVER"
  | "PICKING_IN_PROGRESS"
  | "HANDOVER_READY"
  | "PRETRIP_PENDING"
  | "EMPTY"
  | "ACTIVE"
  | "COMPLETED"

/** Visual state of a route: follows the loading flow of its delivery notes (route-progress.ts). */
function computeState(route: IDeliveryRoute): { state: RouteStateKind; pickingPct: number } {
  if (route.status === "COMPLETED") return { state: "COMPLETED", pickingPct: 100 }
  if (route.status === "IN_PROGRESS") return { state: "ACTIVE", pickingPct: 100 }
  const progress = routeProgress(route)
  if (progress === "WAITING_DRIVER") return { state: "WAITING_DRIVER", pickingPct: 0 }
  if (progress === "PICKING") return { state: "PICKING_IN_PROGRESS", pickingPct: routeLoadedPct(route) }
  if (progress === "HANDOVER_READY") return { state: "HANDOVER_READY", pickingPct: 100 }
  if (progress === "PRETRIP_PENDING") return { state: "PRETRIP_PENDING", pickingPct: 100 }
  // Every delivery note on the route was cancelled or returned.
  return { state: "EMPTY", pickingPct: 0 }
}

/** Canvas Ord_Routes: DotPill tone (amber = waiting on someone, green = ready/done, grey = empty). */
const STATE_TONE: Record<RouteStateKind, Tone> = {
  WAITING_DRIVER: "amber",
  PRETRIP_PENDING: "amber",
  EMPTY: "grey",
  PICKING_IN_PROGRESS: "amber",
  HANDOVER_READY: "green",
  ACTIVE: "blue",
  COMPLETED: "green",
}

/** The order a planned route moves through before it leaves (backend: ASSIGNED -> PENDING_HANDOVER -> LOADED). */
const STAGES: RouteProgress[] = ["WAITING_DRIVER", "PICKING", "HANDOVER_READY", "PRETRIP_PENDING"]

const STAGE_OF: Partial<Record<RouteStateKind, RouteProgress>> = {
  WAITING_DRIVER: "WAITING_DRIVER",
  PICKING_IN_PROGRESS: "PICKING",
  HANDOVER_READY: "HANDOVER_READY",
  PRETRIP_PENDING: "PRETRIP_PENDING",
}

/** Planned route: four short bars (done / current / to do) and the current step in words. */
function StageCell({ current, pickingPct }: Readonly<{ current: RouteProgress; pickingPct: number }>) {
  const { t } = useTranslation(["delivery"])
  const currentIdx = STAGES.indexOf(current)
  return (
    <Box className={classes.progressCell}>
      <Box className={classes.stageBars} aria-hidden>
        {STAGES.map((stage, i) => (
          <Box
            key={stage}
            className={classes.stageBar}
            data-status={i < currentIdx ? "done" : i === currentIdx ? "current" : "todo"}
          />
        ))}
      </Box>
      <Text className={classes.progressText}>
        <Text span className={classes.stepNo}>
          {`${currentIdx + 1}/${STAGES.length}`}
        </Text>
        {` ${t(`delivery:route.card.stage.${current}`)}`}
        {current === "PICKING" && ` · ${pickingPct}%`}
      </Text>
    </Box>
  )
}

/** Route on the road / done: handled stops out of all, with failures named. */
function StopsCell({ route }: Readonly<{ route: IDeliveryRoute }>) {
  const { t } = useTranslation(["delivery"])
  const total = route.totalStops ?? route.stops?.length ?? 0
  const done = route.completedStops ?? 0
  const failed = route.failedStops ?? 0
  const skipped = route.skippedStops ?? 0
  const handled = done + failed + skipped
  if (total === 0) return <Text className={classes.muted}>{DASH}</Text>
  const pct = Math.min(100, (handled / total) * 100)
  return (
    <Box className={classes.progressCell}>
      <Progress value={pct} size={6} radius="xl" color={handled === total ? "green" : "blue"} aria-hidden />
      <Text className={classes.progressText}>
        <Text span className={classes.stepNo}>
          {`${handled}/${total}`}
        </Text>
        {handled === total
          ? ` ${t("delivery:route.card.stops.allDone")}`
          : done > 0 && ` · ${t("delivery:route.card.stops.delivered", { count: done })}`}
        {failed > 0 && (
          <Text span className={classes.failed}>
            {` · ${t("delivery:route.table.failed", { count: failed })}`}
          </Text>
        )}
      </Text>
    </Box>
  )
}

function TwoLine({ main, sub, missing }: Readonly<{ main?: string | null; sub?: string | null; missing: string }>) {
  if (!main) return <Text className={classes.missing}>{missing}</Text>
  return (
    <Box className={classes.twoLine}>
      <Text className={classes.main} lineClamp={1}>
        {main}
      </Text>
      {sub && (
        <Text className={classes.sub} lineClamp={1}>
          {sub}
        </Text>
      )}
    </Box>
  )
}

/** The route (direction) name is what people recognise; the RT- code sits under it. */
function NameCell({ route }: Readonly<{ route: IDeliveryRoute }>) {
  return <RouteNameCell name={route.name} routeNumber={route.routeNumber} />
}

/** Same cell for any list that shows a route (e.g. the handover tab's load queue). */
export function RouteNameCell({ name, routeNumber }: Readonly<{ name?: string | null; routeNumber?: string | null }>) {
  return (
    <Box className={classes.twoLine}>
      <Text className={classes.name} lineClamp={1}>
        {name || routeNumber || "-"}
      </Text>
      {name && routeNumber && (
        <Text className={classes.code} translate="no">
          {routeNumber}
        </Text>
      )}
    </Box>
  )
}

function RouteRow({
  route,
  onEdit,
  onStartHandover,
}: Readonly<{
  route: IDeliveryRoute
  onEdit: (route: IDeliveryRoute) => void
  onStartHandover: (route: IDeliveryRoute) => void
}>) {
  const { t } = useTranslation(["delivery", "common"])
  const navigate = useNavigate()
  const { stateOf } = useDeliveryActions()
  const classLabel = useVehicleClassLabel()
  const { state, pickingPct } = computeState(route)
  const stage = STAGE_OF[state]
  const totalStops = route.totalStops ?? route.stops?.length ?? 0

  const openDetail = () => navigate(`/delivery-routes/${route.id}`)

  const badgeLabel =
    state === "PICKING_IN_PROGRESS"
      ? t("delivery:routeProgress.picking")
      : state === "PRETRIP_PENDING"
        ? t("delivery:routeProgress.pretripPending")
        : state === "EMPTY"
          ? t("delivery:routeProgress.dnCancelled")
          : t(`delivery:route.card.state.${state}`)

  const driverName = route.driver && renderFullName(route.driver) !== "-" ? renderFullName(route.driver) : null
  const vehicleLine = route.vehicle
    ? [route.vehicle.make, route.vehicle.model].filter(Boolean).join(" ") || classLabel(route.vehicle.vehicleClass)
    : null
  const distanceDuration = [
    route.totalDistanceKm != null ? `${route.totalDistanceKm} ${t("delivery:route.unit.km")}` : null,
    route.plannedDurationMinutes != null ? `${route.plannedDurationMinutes} ${t("delivery:route.unit.min")}` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  const assignState = stateOf("routeAssign", route)
  const handoverState = stateOf("handoverCreate", route)
  const assignIsNextStep = state === "WAITING_DRIVER"

  return (
    <Table.Tr className={classes.row} onClick={openDetail}>
      <Table.Td>
        <Box className={classes.actions}>
          <DeliveryRowAction label={t("delivery:route.card.view")} icon={<EyeIcon />} onClick={openDetail} />
          {assignState.applies && (
            <DeliveryRowAction
              label={assignIsNextStep ? t("delivery:route.card.assign") : t("delivery:route.card.change")}
              icon={assignIsNextStep ? <UserPlusIcon /> : <PencilSimpleIcon />}
              state={assignState}
              onClick={() => onEdit(route)}
            />
          )}
          {state === "HANDOVER_READY" && (
            <DeliveryRowAction
              label={t("delivery:route.card.startHandover")}
              icon={<HandshakeIcon />}
              state={handoverState}
              onClick={() => onStartHandover(route)}
            />
          )}
        </Box>
      </Table.Td>
      <Table.Td>
        <NameCell route={route} />
      </Table.Td>
      <Table.Td>
        <RenderText
          className={classes.mono}
          color="var(--nw-text)"
          text={route.plannedDate ? dayjs(route.plannedDate).format("YYYY-MM-DD") : null}
        />
      </Table.Td>
      <Table.Td>
        <DotPill tone={STATE_TONE[state]}>{badgeLabel}</DotPill>
      </Table.Td>
      <Table.Td>
        <TwoLine main={driverName} sub={route.driver?.phone} missing={t("delivery:route.card.unassigned")} />
      </Table.Td>
      <Table.Td>
        <TwoLine main={route.vehicle?.plateNumber} sub={vehicleLine} missing={t("delivery:route.card.unassigned")} />
      </Table.Td>
      <Table.Td>
        {stage ? (
          <StageCell current={stage} pickingPct={pickingPct} />
        ) : state === "ACTIVE" || state === "COMPLETED" ? (
          <StopsCell route={route} />
        ) : (
          <RenderText color="var(--nw-text-3)" text={null} />
        )}
      </Table.Td>
      <Table.Td ta="right">
        <RenderText className={classes.mono} color="var(--nw-text)" text={totalStops} ml="auto" />
      </Table.Td>
      <Table.Td ta="right">
        <RenderText
          className={classes.mono}
          color={distanceDuration ? "var(--nw-text)" : "var(--nw-text-3)"}
          text={distanceDuration || null}
          ml="auto"
        />
      </Table.Td>
    </Table.Tr>
  )
}

/**
 * Delivery routes as one table: actions first, then name, date, state, driver, vehicle,
 * the loading step (planned) or the stop progress (on the road), stops and distance.
 */
export function RouteTable({
  rows,
  onEdit,
  onStartHandover,
}: Readonly<{
  rows: IDeliveryRoute[]
  onEdit: (route: IDeliveryRoute) => void
  onStartHandover: (route: IDeliveryRoute) => void
}>) {
  const { t } = useTranslation(["delivery", "common"])
  return (
    <TableFrame>
      <Table.ScrollContainer minWidth={1180}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={132}>{t("common:actions.action")}</Table.Th>
              <Table.Th miw={200}>{t("delivery:route.table.route")}</Table.Th>
              <Table.Th w={120}>{t("delivery:route.card.date")}</Table.Th>
              <Table.Th w={210}>{t("delivery:route.table.status")}</Table.Th>
              <Table.Th miw={170}>{t("delivery:route.card.driver")}</Table.Th>
              <Table.Th miw={170}>{t("delivery:route.card.vehicle")}</Table.Th>
              <Table.Th miw={210}>{t("delivery:route.table.progress")}</Table.Th>
              <Table.Th w={96} ta="right">
                {t("delivery:route.table.stops")}
              </Table.Th>
              <Table.Th w={150} ta="right">
                {t("delivery:route.card.distanceDuration")}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((route) => (
              <RouteRow key={route.id} route={route} onEdit={onEdit} onStartHandover={onStartHandover} />
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </TableFrame>
  )
}
